import axios, { AxiosProgressEvent, CancelTokenSource } from "axios";
import { apiRequest } from "../../../utils/API";
import {
  IAPIResponseGetDictionaries,
  IDictionary,
  IAntDProTableRequest,
} from "../type/DictionaryManagementType";

//* Get all admin and moderator
export const getDictionaryList = async ({
  params = {},
  filter = {},
}: IAntDProTableRequest) => {
  const queryParams = new URLSearchParams();

  if (params.current) queryParams.append("page", params.current.toString());
  if (params.pageSize) queryParams.append("limit", params.pageSize.toString());
  if (filter.name) queryParams.append("name", filter.name.toString());
  if (params.author) queryParams.append("author", params.author.toString());

  const response = await apiRequest<IAPIResponseGetDictionaries>(
    "GET",
    `/dictionaries?${queryParams.toString()}`
  );

  return response.data;
};

export const createDictionary = async (data: IDictionary) => {
  const payload: Partial<IDictionary> = {
    name: data.name,
    totalVocab: data.totalVocab,
  };

  if (data.author === undefined) {
    payload.author = "";
  } else {
    payload.author = data.author;
  }

  if (data.remark === undefined) {
    payload.remark = "";
  } else {
    payload.remark = data.remark;
  }

  return await apiRequest<IDictionary>("POST", "/dictionaries", payload);
};

export const getDictionary = async (id: string | undefined) => {
  const response = await apiRequest<IAPIResponseGetDictionaries>(
    "GET",
    `/dictionaries/${id}`
  );
  return response.data;
};

export const editDictionary = async (data: IDictionary) => {
  const payload: Partial<IDictionary> = {
    name: data.name,
  };

  if (data.author === undefined) {
    payload.author = "";
  } else {
    payload.author = data.author;
  }

  if (data.remark === undefined) {
    payload.remark = "";
  } else {
    payload.remark = data.remark;
  }

  return await apiRequest<IDictionary>(
    "PATCH",
    `/dictionaries/${data.id}`,
    payload
  );
};

export const deleteDictionary = async (id: string) => {
  const response = await apiRequest<void>(
    "PATCH",
    `/dictionaries/${id}/archive`
  );

  console.log(response);

  return response;
};

export interface ImportError {
  code: number;
  message: string;
  details: Array<{
    sheet: string;
    row: number;
    column: string;
    data: number | string;
    message: string;
  }>;
  stack?: string;
}

export async function importDictionary(
  file: File,
  onUploadProgress: (progress: number) => void,
  onUploadComplete: () => void,
  isUpdate: boolean = false,
  dictionaryId?: string
): Promise<() => void> {
  const formData = new FormData();
  formData.append("file", file);

  const source: CancelTokenSource = axios.CancelToken.source();

  // Determine the correct endpoint based on whether it's an update or create
  const endpoint =
    isUpdate && dictionaryId
      ? `/dictionaries/${dictionaryId}/upload`
      : "/dictionaries/upload";

  try {
    await apiRequest<void>(isUpdate ? "PATCH" : "POST", endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      cancelToken: source.token,
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onUploadProgress(progress);
          if (progress === 100) {
            onUploadComplete();
          }
        }
      },
    });
  } catch (error: any) {
    console.error("API request error:", error);
    throw error;
  }

  // Return a function to cancel the request
  return () => source.cancel("Operation canceled by the user.");
}

export const exportDictionary = async (
  id: string
): Promise<{ data: Blob; filename: string }> => {
  const response = await apiRequest<Blob>(
    "GET",
    `/dictionaries/${id}/exports`,
    {},
    {
      responseType: "blob",
    }
  );

  let filename = `dictionary_${id}.xlsx`; // Default filename

  const contentDisposition = response.headers["content-disposition"];
  if (contentDisposition) {
    // RFC 6266 compliant parsing
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, "");
    }
  }

  return { data: response.data, filename };
};
